import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, Upload, X, User, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FileUploadProps {
  value?: string;
  onChange: (url: string | null) => void;
  onFileSelect?: (file: File) => void;
  disabled?: boolean;
  type?: 'profile' | 'logo';
  className?: string;
}

export const FileUpload = ({ 
  value, 
  onChange, 
  onFileSelect,
  disabled = false,
  type = 'profile',
  className 
}: FileUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onFileSelect) {
      onFileSelect(file);
    }
    // Reset the input value to allow selecting the same file again
    event.target.value = '';
  };

  const handleRemove = () => {
    onChange(null);
  };

  const isProfile = type === 'profile';
  const size = isProfile ? 'h-24 w-24' : 'h-20 w-20';
  const icon = isProfile ? User : Building2;

  return (
    <div className={cn("flex items-center gap-4", className)}>
      <div className={cn("relative", size)}>
        {isProfile ? (
          <Avatar className={size}>
            <AvatarImage src={value || ''} />
            <AvatarFallback>
              <User className="h-8 w-8 text-muted-foreground" />
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className={cn(
            "flex items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/50",
            size
          )}>
            {value ? (
              <img 
                src={value} 
                alt="Company logo" 
                className="h-full w-full object-contain rounded-lg"
              />
            ) : (
              <Building2 className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
        )}
        
      </div>

      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleFileSelect}
          disabled={disabled || isUploading}
        >
          {isUploading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Upload className="mr-2 h-4 w-4" />
          )}
          {value ? 'Change' : 'Upload'} {isProfile ? 'Picture' : 'Logo'}
        </Button>
        
        {value && !disabled ? (
          <button
            onClick={handleRemove}
            className="text-xs text-destructive hover:text-destructive/80 text-left"
          >
            Remove {isProfile ? 'photo' : 'logo'}
          </button>
        ) : (
          <p className="text-xs text-muted-foreground">
            Recommended: {isProfile ? '400x400px' : '200x200px'} • Max 5MB
          </p>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />
    </div>
  );
};